import { useCallback, useState } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { DisplayNameDialogProps } from 'types/Dialog.types';

interface UseDisplayNameDialogReturn extends DisplayNameDialogProps {
  /** Opens the dialog if `deckline` (defaults to the selected deckline) is set. */
  openDialog: (deckline?: EnhancedDeckline | null) => void;
}

/**
 * Hook to manage DisplayNameDialog state and logic.
 * Encapsulates open/close state and the confirm handler that updates the deckline's display_name.
 */
const useDisplayNameDialog = (
  selectedDeckline: EnhancedDeckline | null | undefined,
  updatePageDeckline: <K extends keyof EnhancedDeckline>(
    internalDecklineId: string,
    field: K,
    newValue: EnhancedDeckline[K],
  ) => void,
): UseDisplayNameDialogReturn => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /**
   * Opens the dialog if a deckline is given, or else currently selected. The dialog itself always edits
   * the selected deckline, so a caller passing `deckline` must also make it the selection (e.g. a
   * right-click that selects and opens in one step, before `selectedDeckline` has re-rendered).
   * @param deckline - The deckline being edited (defaults to the selected deckline)
   */
  const openDialog = useCallback(
    (deckline: EnhancedDeckline | null | undefined = selectedDeckline) => {
      if (deckline) setIsDialogOpen(true);
    },
    [selectedDeckline],
  );

  /** Persists the new display_name to the selected deckline and closes the dialog. */
  const handleConfirm = useCallback(
    (displayName: string | null) => {
      if (selectedDeckline) {
        updatePageDeckline(selectedDeckline.metadata.internalId, 'display_name', displayName);
      }
      setIsDialogOpen(false);
    },
    [selectedDeckline, updatePageDeckline],
  );

  return {
    isDialogOpen,
    setIsDialogOpen,
    openDialog,
    handleConfirm,
    currentDisplayName: selectedDeckline?.display_name ?? null,
    kpiName: selectedDeckline?.kpi_name ?? '',
  };
};

export default useDisplayNameDialog;
